import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  IconButton,
  Stack,
  Paper,
  Grid,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import {
  Edit as EditIcon,
  ExpandMore as ExpandMoreIcon,
  Flag as FlagIcon,
  Warning as AlertIcon,
  Person as PersonIcon,
} from '@mui/icons-material';
import { DriverData, EXPERIENCE_RADIUS_OPTIONS } from './types';
import { getBooleanState, getSafetyState, formatDate } from './utils';
import { SnapshotEditDialog } from './SnapshotEditDialog';

interface SnapshotTabProps {
  driverData: DriverData | null;
  onSave: (data: Partial<DriverData>) => void;
  isLoading?: boolean;
}

export const SnapshotTab: React.FC<SnapshotTabProps> = ({ driverData, onSave, isLoading = false }) => {
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [displayedData, setDisplayedData] = useState<DriverData | null>(driverData);

  // Sync displayedData with incoming driverData prop
  useEffect(() => {
    setDisplayedData(driverData);
  }, [driverData]);

  if (!displayedData) {
    return <Typography>Loading...</Typography>;
  }

  const renderBooleanState = (value: boolean | null) => {
    const state = getBooleanState(value);
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        {state.icon}
        <Typography variant="body2">{state.text}</Typography>
      </Box>
    );
  };

  const renderSafetyState = (value: boolean | null, trueLabel?: string, falseLabel?: string) => {
    const state = getSafetyState(value, trueLabel, falseLabel);
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        {state.icon}
        <Typography variant="body2">{state.text}</Typography>
      </Box>
    );
  };

  // Convert experience radius enum to user-friendly label
  const getExperienceRadiusLabel = (enumValue: string | undefined) => {
    if (!enumValue) return 'Not specified';
    const option = EXPERIENCE_RADIUS_OPTIONS.find(opt => opt.enum === enumValue);
    return option?.label || enumValue;
  };

  return (
    <Box sx={{ p: 2 }}>
      {/* Header with Edit Button */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h6">Driver Profile Snapshot</Typography>
        <IconButton
          size="small"
          onClick={() => setEditDialogOpen(true)}
          color="primary"
          sx={{ bgcolor: 'primary.50' }}
        >
          <EditIcon fontSize="small" />
        </IconButton>
      </Box>

      <Stack spacing={2}>
        {/* Experience Section */}
        <Accordion defaultExpanded>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <FlagIcon sx={{ mr: 1, color: 'primary.main' }} />
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              Experience
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Grid container spacing={3} sx={{ width: '100%' }}>
              <Grid item xs={12} md={6}>
                <Paper sx={{ p: 2, bgcolor: '#fafafa', border: '1px solid #e0e0e0', borderRadius: 1 }}>
                  <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
                    Experience Radius
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {getExperienceRadiusLabel(displayedData.experienceRadius)}
                  </Typography>
                </Paper>
              </Grid>

              <Grid item xs={12} md={6}>
                <Paper sx={{ p: 2, bgcolor: '#fafafa', border: '1px solid #e0e0e0', borderRadius: 1 }}>
                  <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
                    Canada Experience
                  </Typography>
                  {renderBooleanState(displayedData.hasCanadaExperience)}
                  {displayedData.canadaExperience && (
                    <Typography variant="caption" sx={{ display: 'block', mt: 1 }}>
                      {displayedData.canadaExperience}
                    </Typography>
                  )}
                </Paper>
              </Grid>

              <Grid item xs={12} md={6}>
                <Paper sx={{ p: 2, bgcolor: '#fafafa', border: '1px solid #e0e0e0', borderRadius: 1 }}>
                  <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
                    USA Experience
                  </Typography>
                  {renderBooleanState(displayedData.hasUsaExperience)}
                  {displayedData.usaExperience && (
                    <Typography variant="caption" sx={{ display: 'block', mt: 1 }}>
                      {displayedData.usaExperience}
                    </Typography>
                  )}
                </Paper>
              </Grid>

              <Grid item xs={12} md={6}>
                <Paper sx={{ p: 2, bgcolor: '#fafafa', border: '1px solid #e0e0e0', borderRadius: 1 }}>
                  <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
                    International Experience
                  </Typography>
                  {renderBooleanState(displayedData.hasInternationalExperience)}
                  {displayedData.internationalExperience && (
                    <Typography variant="caption" sx={{ display: 'block', mt: 1 }}>
                      {displayedData.internationalExperience}
                    </Typography>
                  )}
                </Paper>
              </Grid>
            </Grid>
          </AccordionDetails>
        </Accordion>

        {/* Safety & Citizenship Section */}
        <Accordion defaultExpanded>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <AlertIcon sx={{ mr: 1, color: 'primary.main' }} />
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              Safety & Citizenship
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Grid container spacing={3} sx={{ width: '100%' }}>
              <Grid item xs={12} md={6}>
                <Paper sx={{ p: 2, bgcolor: '#fafafa', border: '1px solid #e0e0e0', borderRadius: 1 }}>
                  <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
                    Recent Accidents (3 Years)
                  </Typography>
                  {renderSafetyState(displayedData.hasRecentAccident)}
                  {displayedData.recentAccidentDescription && (
                    <Typography variant="caption" sx={{ display: 'block', mt: 1 }}>
                      {displayedData.recentAccidentDescription}
                    </Typography>
                  )}
                </Paper>
              </Grid>

              <Grid item xs={12} md={6}>
                <Paper sx={{ p: 2, bgcolor: '#fafafa', border: '1px solid #e0e0e0', borderRadius: 1 }}>
                  <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
                    Recent Tickets (3 Years)
                  </Typography>
                  {renderSafetyState(displayedData.hasRecentTicket)}
                  {displayedData.recentTicketDescription && (
                    <Typography variant="caption" sx={{ display: 'block', mt: 1 }}>
                      {displayedData.recentTicketDescription}
                    </Typography>
                  )}
                </Paper>
              </Grid>

              <Grid item xs={12} md={6}>
                <Paper sx={{ p: 2, bgcolor: '#fafafa', border: '1px solid #e0e0e0', borderRadius: 1 }}>
                  <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
                    Drug/Alcohol Testing (3 Years)
                  </Typography>
                  {renderSafetyState(displayedData.drugAlcohol3Year)}
                </Paper>
              </Grid>

              <Grid item xs={12} md={6}>
                <Paper sx={{ p: 2, bgcolor: '#fafafa', border: '1px solid #e0e0e0', borderRadius: 1 }}>
                  <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
                    Canadian Citizen
                  </Typography>
                  {renderBooleanState(displayedData.canadianCitizen)}
                </Paper>
              </Grid>

              <Grid item xs={12} md={6}>
                <Paper sx={{ p: 2, bgcolor: '#fafafa', border: '1px solid #e0e0e0', borderRadius: 1 }}>
                  <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
                    Can Live & Work in Canada
                  </Typography>
                  {renderBooleanState(displayedData.canadaLiveAndWork)}
                </Paper>
              </Grid>
            </Grid>
          </AccordionDetails>
        </Accordion>

        {/* Passport & Identification Section */}
        <Accordion defaultExpanded>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <PersonIcon sx={{ mr: 1, color: 'primary.main' }} />
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              Passport & Identification
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Grid container spacing={3} sx={{ width: '100%' }}>
              <Grid item xs={12} md={6}>
                <Paper sx={{ p: 2, bgcolor: '#fafafa', border: '1px solid #e0e0e0', borderRadius: 1 }}>
                  <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
                    Can Enter USA
                  </Typography>
                  {renderBooleanState(displayedData.canEnterUsa)}
                </Paper>
              </Grid>

              <Grid item xs={12} md={6}>
                <Paper sx={{ p: 2, bgcolor: '#fafafa', border: '1px solid #e0e0e0', borderRadius: 1 }}>
                  <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
                    Valid Passport
                  </Typography>
                  {renderBooleanState(displayedData.hasValidPassport)}
                </Paper>
              </Grid>

              <Grid item xs={12} md={6}>
                <Paper sx={{ p: 2, bgcolor: '#fafafa', border: '1px solid #e0e0e0', borderRadius: 1 }}>
                  <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
                    Passport Number
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {displayedData.passportNumber || 'Not provided'}
                  </Typography>
                </Paper>
              </Grid>

              <Grid item xs={12} md={6}>
                <Paper sx={{ p: 2, bgcolor: '#fafafa', border: '1px solid #e0e0e0', borderRadius: 1 }}>
                  <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
                    Passport Expiry
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {displayedData.passportExpiryDate ? formatDate(displayedData.passportExpiryDate) : 'Not provided'}
                  </Typography>
                </Paper>
              </Grid>

              <Grid item xs={12} md={6}>
                <Paper sx={{ p: 2, bgcolor: '#fafafa', border: '1px solid #e0e0e0', borderRadius: 1 }}>
                  <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
                    Passport Issuing Country
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {displayedData.passportCountryOrigin || 'Not provided'}
                  </Typography>
                </Paper>
              </Grid>

              <Grid item xs={12} md={6}>
                <Paper sx={{ p: 2, bgcolor: '#fafafa', border: '1px solid #e0e0e0', borderRadius: 1 }}>
                  <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
                    FAST Pass
                  </Typography>
                  {renderBooleanState(displayedData.hasFastPass)}
                  {displayedData.fastPassNumber && (
                    <Typography variant="caption" sx={{ display: 'block', mt: 1 }}>
                      #{displayedData.fastPassNumber}
                    </Typography>
                  )}
                </Paper>
              </Grid>

              <Grid item xs={12} md={6}>
                <Paper sx={{ p: 2, bgcolor: '#fafafa', border: '1px solid #e0e0e0', borderRadius: 1 }}>
                  <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
                    US DOT Number
                  </Typography>
                  {renderBooleanState(displayedData.hasUsDotNumber)}
                  {displayedData.usDotNumber && (
                    <Typography variant="caption" sx={{ display: 'block', mt: 1 }}>
                      {displayedData.usDotNumber}
                    </Typography>
                  )}
                </Paper>
              </Grid>
            </Grid>
          </AccordionDetails>
        </Accordion>
      </Stack>

      {/* Edit Dialog */}
      <SnapshotEditDialog
        open={editDialogOpen}
        driverData={displayedData}
        onClose={() => setEditDialogOpen(false)}
        onSave={(data) => {
          // Update displayed data immediately for instant feedback
          if (displayedData) {
            setDisplayedData({ ...displayedData, ...data });
          }
          // Then call parent's onSave to persist to backend
          onSave(data);
          setEditDialogOpen(false);
        }}
        isLoading={isLoading}
      />
    </Box>
  );
};
